import React from 'react';
import {
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Table,
  Collapse,
  Box,
  Typography,
  Checkbox,
  Link,
  Tooltip,
  IconButton,
  Chip,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { bookingStatuses } from 'utils';
import { BOOKING_STATUSES, CANCELED_BOOKING_STATUS_CODES } from 'utils/constants';

const formatPrice = (product_price) => {
  if (!product_price) return null;
  const amount = Number(product_price.subunits) / 100;
  return `${amount.toLocaleString()} ${product_price.iso || ''}`.trim();
};

// Suma el valor de los pack_items para el pie del detalle expandible. Usa el
// iso del primer item con precio, ya que todos comparten moneda.
const packItemsTotal = (items) => {
  const iso = items.find((item) => item.product_price?.iso)?.product_price?.iso;
  const subunits = items.reduce(
    (sum, item) => sum + Number(item.product_price?.subunits || 0),
    0
  );
  if (!iso || subunits === 0) return null;
  return formatPrice({ subunits, iso });
};

// Fila de una venta (1 orden de ML, o 1 envio con varias ordenes agrupadas
// via mercadoflex_pack_grouping - ver docs/2026-08-13-mercadoflex-pack-
// grouping.md). El detalle expandible solo tiene sentido cuando sale.isPack().
const SaleRow = ({
  sale,
  index,
  classes,
  translate,
  citySelected,
  checked,
  disabledCheckbox,
  disabledReason,
  onCheckChange,
  expanded,
  onToggleExpand,
}) => {
  const isPack = sale.isPack();
  // Orden con un booking anterior cancelado/expirado: ya es seleccionable de
  // nuevo (ver MercadoFlexSale#isReadyToDispatch), pero se avisa que se va a
  // crear un servicio nuevo en vez de reactivar el anterior.
  const wasCanceledAndRelaunchable =
    ![null, undefined].includes(sale?.booking_id) &&
    CANCELED_BOOKING_STATUS_CODES.includes(sale.status_cd);

  return (
    <>
      <TableRow
        hover
        style={{
          opacity: citySelected && citySelected !== sale?.order?.city_id && '0.7',
        }}
      >
        <TableCell style={{ width: 32 }}>
          {isPack ? (
            <IconButton
              aria-label="expand row.sale"
              size="small"
              onClick={onToggleExpand}
            >
              {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          ) : null}
        </TableCell>
        <TableCell>
          {sale.isReadyToDispatch() ? (
            <Tooltip
              title={
                sale.booking_id
                  ? translate('app.sales.noCreateService.reason')
                  : translate('app.sales.noCreateService.reason2')
              }
            >
              <InfoOutlinedIcon fontSize="small" />
            </Tooltip>
          ) : disabledCheckbox ? (
            <Tooltip title={disabledReason}>
              <span>
                <Checkbox
                  checked={checked || false}
                  disabled
                  color="primary"
                  size="small"
                  style={{ padding: 2 }}
                />
              </span>
            </Tooltip>
          ) : wasCanceledAndRelaunchable ? (
            <Tooltip title={translate('app.sales.relaunch.warning')}>
              <Checkbox
                checked={checked || false}
                onChange={onCheckChange}
                name={index.toString()}
                color="primary"
                size="small"
                style={{ padding: 2 }}
              />
            </Tooltip>
          ) : (
            <Checkbox
              checked={checked || false}
              onChange={onCheckChange}
              name={index.toString()}
              color="primary"
              size="small"
              style={{ padding: 2 }}
            />
          )}
        </TableCell>
        <TableCell className={classes.status}>
          <span className={sale.getSaleClass()} style={{ padding: '3px 7px' }}>
            {translate(sale.getSaleStatus())}
          </span>
        </TableCell>
        <TableCell>
          {sale.external_id || ' - '}
          {isPack && (
            <Chip
              size="small"
              label={translate('app.sales.table.pack.badge').replace('{count}', sale.pack_size - 1)}
              style={{ marginLeft: 6 }}
            />
          )}
        </TableCell>
        <TableCell>{sale.order_id || ' - '}</TableCell>

        <TableCell className={classes.address}>{sale.product_name || '-'}</TableCell>
        <TableCell>
          {sale.date_of_purchase ? (
            <span className={classes.date}>
              {sale.date_of_purchase?.slice(0, 10).split('-').reverse().join('/')}
              , {sale.date_of_purchase?.slice(11, 16)}
            </span>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell>
          {sale.shipping_estimated_date ? (
            <span className={classes.date}>
              {sale.shipping_estimated_date?.slice(0, 10).split('-').reverse().join('/')}
              , {sale.shipping_estimated_date?.slice(11, 16)}
            </span>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell>
          {sale.order?.integrator === 'mercadoflex' ? (
            <img
              src="https://static.pibox.app/images/integrations/mercadoflex.png"
              style={{ width: '85px' }}
              alt="mercadoflex"
            />
          ) : sale.order?.photo_url ? (
            <img
              src={sale.order.photo_url}
              style={{ width: '85px' }}
              alt={sale.order?.integrator || ''}
            />
          ) : sale.order?.integrator ? (
            <p>{sale.order.integrator}</p>
          ) : (
            ' - '
          )}
        </TableCell>
        <TableCell>{sale.order?.city_name || ' - '}</TableCell>
        <TableCell>
          {sale.status_cd !== null ? (
            <span className={bookingStatuses(sale.status_cd)} style={{ padding: '3px 7px' }}>
              {translate(BOOKING_STATUSES[sale.status_cd])}
            </span>
          ) : (
            ' - '
          )}
        </TableCell>
        <TableCell>
          <span style={{ fontSize: '0.94em' }}>{sale.booking_id || '-'}</span>
        </TableCell>
        <TableCell className={classes.address}>
          {sale.booking_id ? (
            <Link
              href={`/bookings/${sale.booking_id}`}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              underline="hover"
            >
              {sale.destination.address || ' - '}
            </Link>
          ) : (
            <p style={{ margin: 0 }}>{sale.destination.address || ' - '}</p>
          )}
        </TableCell>
        <TableCell className={classes.address}>
          <p style={{ margin: 0 }}>{sale.shipping_id || sale.external_id || ' - '}</p>
        </TableCell>
        <TableCell className={classes.address}>
          <p style={{ margin: 0 }}>{sale.quantity || ' - '}</p>
        </TableCell>
        <TableCell className={classes.address}>
          <p style={{ margin: 0 }}>{sale.order.order_type || ' - '}</p>
        </TableCell>
      </TableRow>
      {isPack && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={16}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box p="1rem 2rem 2rem">
                <Typography variant="subtitle2" gutterBottom>
                  {translate('app.sales.table.pack.title')}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{translate('app.sales.table.pack.itemId')}</TableCell>
                      <TableCell>{translate('app.sales.table.pack.orderId')}</TableCell>
                      <TableCell>{translate('app.sales.table.pack.product')}</TableCell>
                      <TableCell>{translate('app.sales.table.pack.quantity')}</TableCell>
                      <TableCell>{translate('app.sales.table.pack.value')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sale.packItems().map((item) => (
                      <TableRow key={item.external_id}>
                        <TableCell>{item.external_id}</TableCell>
                        <TableCell>{item.order_id || ' - '}</TableCell>
                        <TableCell>{item.product_name || ' - '}</TableCell>
                        <TableCell>{item.quantity || ' - '}</TableCell>
                        <TableCell>{formatPrice(item.product_price) || ' - '}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box display="flex" justifyContent="space-between">
                  {sale.pack_id && (
                    <Typography variant="caption" color="textSecondary">
                      {translate('app.sales.table.pack.packId')}: {sale.pack_id}
                    </Typography>
                  )}
                  {packItemsTotal(sale.packItems()) && (
                    <Typography variant="caption" color="textSecondary">
                      {translate('app.sales.table.pack.total')}: {packItemsTotal(sale.packItems())}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default SaleRow;
